import { Button, message, Space, Spin } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import GeneralJobPostingForm, {
  GeneralJobPostingFormValues,
} from '../../components/GeneralJobPostingForm';
import PageContent from '../../components/PageContent';
import useAllowedUsersMutation from '../../hooks/useAllowedUsersMutation';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePermissionCheckerQuery from '../../hooks/usePermissionCheckerQuery';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { useSeniorityLevels } from '../../hooks/useSeniorityLevels';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import useUpdateVacancyMutation from '../../hooks/useUpdateVacancyMutation';
import { withRouteChange } from '../../services/APM';
import {
  JobPostingId,
  PaginatedResponse,
  SankhyaJobPostingCollection,
  VixtingJobPostingCollection,
} from '../../types';
import { mapVixtingCompany, mapVixtingJobRole, mapVixtingDepartment } from '../../utils';
import { ConfirmSelfRemoveModal } from './AllowedUsersForm';

const STALE_TIME = 1000 * 60 * 5;

const GeneralForm = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();
  //#region Form watch
  const [form] = Form.useForm();
  const selectedCompany: string = Form.useWatch('sankhyaCompany', form);
  const selectedJobRole: string = Form.useWatch('sankhyaJobRole', form);
  const recruiterValue = Form.useWatch('recruiter', form);
  const managerValue = Form.useWatch('manager', form);
  const relatedValue = Form.useWatch('related', form);
  const [setIsDirty] = useDiscardChangesModal(form);
  const [leavePage, setLeavePage] = useState<boolean>(false);
  //#endregion
  //#region Allowed users
  const [confirmSelfRemoveModalIsOpen, setConfirmSelfRemoveModalIsOpen] = useState<boolean>(false);
  const [selfRemove, setSelfRemove] = useState(false);
  //#endregion
  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const integration: 'sankhya' | 'vixting' = config.data?.enableVixtingAdmission
    ? 'vixting'
    : 'sankhya';

  const { seniorityLevels, defaultSelectedSeniorityLevelID } = useSeniorityLevels();
  const sankhyaJobPosting = useAPIQuery(
    [
      '/sankhya/sankhya-job-postings/',
      {
        params: {
          job_posting_id: id,
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      select(data: PaginatedResponse<SankhyaJobPostingCollection>) {
        return data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const vixtingJobPosting = useAPIQuery(
    [
      '/vixting/vixting-job-postings/',
      {
        params: {
          job_posting_id: id,
        },
      },
    ],
    {
      enabled: config.data?.enableVixtingAdmission,
      select(data: PaginatedResponse<VixtingJobPostingCollection>) {
        return data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const integrationJobPosting = integration === 'vixting' ? vixtingJobPosting : sankhyaJobPosting;

  const sankhyaCompaniesPages = useInfiniteAPIQuery(
    [
      '/sankhya/companies/',
      {
        params: {
          offsetPage: '0',
          fieldsetList: 'CODEMP',
          joinFieldsetList: 'NOMEFANTASIA',
          sort: 'CODEMP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaCompanies = usePrefetchPages(sankhyaCompaniesPages, (data) => data.results);

  const sankhyaJobRolesPages = useInfiniteAPIQuery(
    [
      '/sankhya/job-roles/',
      {
        params: {
          fieldsetList: 'CODCARGO,DESCRCARGO',
          filter: {
            ATIVO: 'S',
          },
          sort: 'CODCARGO',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaJobRoles = usePrefetchPages(sankhyaJobRolesPages, (data) => data.results);

  const sankhyaDepartmentsPages = useInfiniteAPIQuery(
    [
      '/sankhya/departments/',
      {
        params: {
          fieldsetList: 'CODDEP,DESCRDEP',
          filter: {
            ATIVO: 'S',
            ANALITICO: 'S',
          },
          sort: 'CODDEP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaDepartments = usePrefetchPages(sankhyaDepartmentsPages, (data) => data.results);

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar empresas!'));
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar filiais da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar unidades de negócio da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompany,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar cargos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const user = useAPIQuery('/users/me/');

  const recruitersPages = useInfiniteAPIQuery([
    '/recruiters/',
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const recruiters = usePrefetchPages(recruitersPages, (data) => data.results);

  const recruiterUserId = recruiters.find(({ type }) => type === 'RECRUITER')?.user.id;
  const managerUserId = recruiters.find(({ type }) => type === 'MANAGER')?.user.id;
  const relatedUserIds = recruiters
    .filter(({ type }) => type === 'RELATED')
    .map(({ user }) => user.id);
  const observerUserIds = recruiters
    .filter(({ type }) => type === 'OBSERVER')
    .map(({ user }) => user.id);

  // Removing staff Users
  const recruiterInitialValue = users.find((user) => user.id === recruiterUserId)?.id;
  const managerInitialValue = users.find((user) => user.id === managerUserId)?.id;
  const relatedInitialValues = users
    .filter((user) => relatedUserIds.includes(user.id))
    .map((user) => user.id);
  const observersInitialValues = users
    .filter((user) => observerUserIds.includes(user.id))
    .map((user) => user.id);

  const recruiterUserOptions = users.filter(
    (user) => user.is_active || user.id === recruiterInitialValue
  );
  const managerUserOptions = users.filter(
    (user) => user.is_active || user.id === managerInitialValue
  );
  const relatedUserOptions = users.filter(
    (user) => user.is_active || relatedInitialValues.includes(user.id)
  );
  const observersUserOptions = users.filter(
    (user) => user.is_active || observersInitialValues.includes(user.id)
  );

  const updateVacancy = useUpdateVacancyMutation();

  const createRecruiters = useAllowedUsersMutation();

  const mutation = useAPIMutation(
    async (variables: GeneralJobPostingFormValues, api) => {
      await updateVacancy.mutateAsync({
        jobPostingId: id,
        jobPostingFields: {
          name: variables.name,
          positions: variables.positions,
          start_salary_range: variables.startSalaryRange ?? null,
          end_salary_range: variables.endSalaryRange ?? null,
          limit_days: variables.limitDays,
          seniority_level: variables.seniorityLevel ?? null,
          work_model: variables.workModel ?? null,
          hire_model: variables.hireModel ?? null,
        },
      });

      await createRecruiters.mutateAsync({
        recruiter: variables.recruiter,
        manager: variables.manager,
        related: variables.related,
        observers: variables.observers,
        jobPostingId: id,
      });

      const affiliateData = config.data?.enableVixtingAdmission
        ? { affiliate: variables.affiliate || variables.sankhyaCompany || '' }
        : {};

      const unitBusinessData =
        config.data?.enableVixtingAdmission && config.data?.enableVixtingUnitBusiness
          ? { unit_business: variables.unitBusiness || '' }
          : {};

      if (config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission) {
        if (integrationJobPosting.data) {
          await api.patch(
            `/${integration}/${integration}-job-postings/${integrationJobPosting.data?.id}/`,
            {
              company: variables.sankhyaCompany || '',
              job_role: variables.sankhyaJobRole || '',
              department: variables.sankhyaDepartment || '',
              ...affiliateData,
              ...unitBusinessData,
            }
          );
        } else {
          await api.post(`/${integration}/${integration}-job-postings/`, {
            job_posting: id,
            company: variables.sankhyaCompany || '',
            job_role: variables.sankhyaJobRole || '',
            department: variables.sankhyaDepartment || '',
            ...affiliateData,
            ...unitBusinessData,
          });
        }
      }
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Vaga atualizada'));

        if (selfRemove) {
          navigate({ pathname: '/:tenant/opportunities/', params: { tenant } });
        } else if (leavePage) {
          navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } });
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(`/job-postings/${id}/`);
        await queryClient.invalidateQueries(`/${integration}/${integration}-job-postings/`);
        await queryClient.invalidateQueries('/recruiters/');
      },
    }
  );

  const selectedJob = vixtingJobRoles.find(
    (jobRole) => jobRole.id?.toString() === selectedJobRole?.toString()
  );

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: selectedCompany,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!selectedCompany &&
        !!selectedJob?.linkToAllDepartment,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar departamentos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const viewAllVacanciesPermission = usePermissionCheckerQuery(['view_all_vacancies']);

  const formArgs = {
    enableIntegrationAdmission:
      config.data?.enableSankhyaAdmission || config.data?.enableVixtingAdmission,
    sankhyaCompanies: config.data?.enableVixtingAdmission
      ? mapVixtingCompany(vixtingCompanies)
      : sankhyaCompanies,
    sankhyaJobRoles: config.data?.enableVixtingAdmission
      ? mapVixtingJobRole(vixtingJobRoles)
      : sankhyaJobRoles,
    sankhyaDepartments: config.data?.enableVixtingAdmission
      ? mapVixtingDepartment(
          selectedJob?.linkToAllDepartment ? vixtingAllDepartments : selectedJob?.departments || []
        )
      : sankhyaDepartments,
    affiliates: config.data?.enableVixtingAdmission ? vixtingAffiliates : [],
    unitBusinesses: config.data?.enableVixtingAdmission ? vixtingUnitBusinesses : [],
    seniority_levels: seniorityLevels,
    recruiterUserOptions,
    managerUserOptions,
    relatedUserOptions,
    observersUserOptions,
    user: user.data,
  };

  return (
    <ErrorResult error={jobPosting.error || user.error}>
      <PageContent>
        <Spin spinning={jobPosting.isLoading || user.isLoading}>
          <ConfirmSelfRemoveModal
            key="ConfirmSelfRemove"
            open={confirmSelfRemoveModalIsOpen}
            okButtonProps={{ danger: true, type: 'default' }}
            onOk={() => {
              setConfirmSelfRemoveModalIsOpen(false);
              setSelfRemove(true);
              form.submit();
            }}
            onCancel={() => {
              setConfirmSelfRemoveModalIsOpen(false);
              setIsDirty(form.isFieldsTouched());
            }}
            onSuccess={() => {
              setConfirmSelfRemoveModalIsOpen(false);
            }}
            destroyOnClose
          />
          <Helmet>
            <title>
              {t('Configuração da vaga')} | {jobPosting.data?.name || '...'} |{' '}
              {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <Form<GeneralJobPostingFormValues>
            form={form}
            layout="vertical"
            key={jobPosting.data?.id}
            validateMessages={{
              required: t('Campo obrigatório.'),
            }}
            onFinish={(variables) => {
              if (
                !selfRemove &&
                !viewAllVacanciesPermission.data &&
                ![recruiterValue, managerValue, ...relatedValue].includes(user.data?.id)
              ) {
                setConfirmSelfRemoveModalIsOpen(true);
              } else {
                mutation.mutate({ ...variables });
              }
            }}
            initialValues={{
              name: jobPosting.data?.name || '',
              positions: jobPosting.data?.positions || 1,
              startSalaryRange: jobPosting.data?.start_salary_range,
              endSalaryRange: jobPosting.data?.end_salary_range,
              seniorityLevel: jobPosting.data?.seniority_level || defaultSelectedSeniorityLevelID,
              limitDays: jobPosting.data?.limit_days || 1,
              workModel: jobPosting.data?.work_model,
              hireModel: jobPosting.data?.hire_model,
              sankhyaCompany: integrationJobPosting.data?.company || undefined,
              sankhyaJobRole: integrationJobPosting.data?.job_role || undefined,
              sankhyaDepartment: integrationJobPosting.data?.department || undefined,
              recruiter: recruiterInitialValue,
              manager: managerInitialValue,
              related: relatedInitialValues,
              observers: observersInitialValues,
              affiliate: (integrationJobPosting.data as VixtingJobPostingCollection)?.affiliate,
              unitBusiness:
                (integrationJobPosting.data as VixtingJobPostingCollection)?.unit_business ||
                undefined,
            }}
            onValuesChange={(changedValues, values) => {
              if (changedValues.recruiter) {
                form.setFieldsValue({
                  ...values,
                  manager: changedValues.recruiter === values.manager ? undefined : values.manager,
                  related: values.related?.filter((userId) => userId !== changedValues.recruiter),
                  observers: values.observers?.filter(
                    (userId) => userId !== changedValues.recruiter
                  ),
                });
              }
              if (changedValues.manager) {
                form.setFieldsValue({
                  ...values,
                  related: values.related?.filter((userId) => userId !== changedValues.manager),
                  observers: values.observers?.filter((userId) => userId !== changedValues.manager),
                });
              }
              if (changedValues.related) {
                form.setFieldsValue({
                  observers: values.observers?.filter(
                    (userId) => !changedValues.related.includes(userId)
                  ),
                });
              }
              setIsDirty(form.isFieldsTouched());
            }}
          >
            <Space direction="vertical" size={16}>
              <GeneralJobPostingForm {...formArgs} />
              <Form.Footer>
                <Form.FooterEnd>
                  <Button
                    onClick={() =>
                      navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } })
                    }
                  >
                    <Trans>Cancelar</Trans>
                  </Button>
                  <Button
                    htmlType="submit"
                    loading={mutation.isLoading}
                    onClick={() => setLeavePage(true)}
                  >
                    <Trans>Salvar e Sair</Trans>
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={mutation.isLoading}
                    data-testid={`save-edit-opportunity-${id}`}
                  >
                    <Trans>Salvar</Trans>
                  </Button>
                </Form.FooterEnd>
              </Form.Footer>
            </Space>
          </Form>
          <div style={{ height: 80 }} />
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default withRouteChange()(GeneralForm);
